import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { ButtonRoundSm, InputInfoSM } from '@apps-common/components/elements'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { SiteDetailsModal } from '@apps-common/components/modals/site/SiteDetailsModal'
import { commonIcons } from '@apps-common/config/icons'
import { IActivationServiceRequestCreateModel } from '@apps-common/types/portal/service-request'
import { ISiteDetailsModel } from '@apps-common/types/portal/site-details'

interface IProps {
  loading?: boolean
  updating?: boolean
  site?: ISiteDetailsModel
  error?: boolean
  refetch: () => void
  clear: () => void
}

export const ActivationSiteView: React.FC<IProps> = ({ loading, updating, site, error, refetch, clear }) => {
  const { getValues } = useFormContext<IActivationServiceRequestCreateModel>()

  const { unitId, existingUnitId } = getValues()

  const hasSite = useMemo(() => site && !error, [site, loading, error])

  const buildAddressBody = () => {
    const addressLines = []

    if (site?.streetAddress1) {
      addressLines.push(<div key='confirmation-site-details-street-address-1'>{site.streetAddress1}</div>)
    }

    if (site?.streetAddress2) {
      addressLines.push(<div key='confirmation-site-details-street-address-2'>{site.streetAddress2}</div>)
    }

    if (site?.streetAddress3) {
      addressLines.push(<div key='confirmation-site-details-street-address-3'>{site.streetAddress3}</div>)
    }

    if (addressLines.length === 0) {
      return <commonIcons.dash />
    }

    return addressLines
  }

  return (
    <InputInfoSM
      id='site'
      hideNoEditIcon
      showLabel
      label='Site'
      value={
        <div className='relative min-w-[140px] rounded-3xl text-[0.9em] px-5 py-4 text-left bg-surface-2 border border-border'>
          <div className='relative flex items-center justify-end gap-[5px]'>
            <ModalButton
              component={SiteDetailsModal}
              disabled={loading}
              componentProps={{
                unitId: existingUnitId || unitId
              }}
              confirmCallback={() => refetch()}
            >
              <ButtonRoundSm
                type='div'
                loading={loading || updating}
                className={hasSite ? 'action-neutral' : 'action-success'}
                tooltip={{
                  id: 'unit-activation-template-add-site-details-tooltip',
                  message: site && !error ? 'Edit site details' : 'Add site details'
                }}
              >
                {hasSite ? <commonIcons.edit /> : <commonIcons.add />}
              </ButtonRoundSm>
            </ModalButton>
            {hasSite && (
              <ModalButton
                component={ConfirmModal}
                disabled={loading || updating}
                classes=''
                componentProps={{
                  text: 'Delete',
                  description: "Confirm you would like to delete this devices' site details?",
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => clear()}
                tooltip={{ id: 'unit-activation-template-delete-site-details-tooltip', message: 'Delete' }}
              >
                <ButtonRoundSm type='div' className='action-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
          </div>
          <InputInfoSM
            id='siteName'
            showLabel
            hideNoEditIcon
            loading={loading || error}
            label='Site name'
            value={site?.siteName || <commonIcons.dash />}
          />
          <InputInfoSM id='address' showLabel hideNoEditIcon loading={loading || error} label='Address' value={buildAddressBody()} />
          <InputInfoSM
            id='city'
            showLabel
            hideNoEditIcon
            loading={loading || error}
            label='City'
            value={site?.city || <commonIcons.dash className='mt-[3px]' />}
          />
          <InputInfoSM
            id='phone'
            showLabel
            hideNoEditIcon
            loading={loading || error}
            label='Phone'
            value={site?.phone || <commonIcons.dash className='mt-[3px]' />}
          />
        </div>
      }
      className='border-none !p-0 !opacity-100'
    />
  )
}
